/**
 * Chat utility functions for ChatWindow component
 * These pure functions handle calculations and data transformations
 * that don't depend on component state or props.
 */

// UI dimensions
export const UI_DIMENSIONS = {
  MIN_CHAT_WIDTH_RATIO_DESKTOP: 1 / 3,
  MIN_CHAT_WIDTH_RATIO_TABLET: 1 / 2,
  CHAT_OFFSET: 63
}

export const getMinWidth = () => {
  if (globalThis.innerWidth >= 1200) {
    return globalThis.innerWidth * UI_DIMENSIONS.MIN_CHAT_WIDTH_RATIO_DESKTOP
  } else if (globalThis.innerWidth >= 768) {
    return globalThis.innerWidth * UI_DIMENSIONS.MIN_CHAT_WIDTH_RATIO_TABLET
  } else {
    return getMaxWidth()
  }
}

export const getMaxWidth = () => {
  return (
    globalThis.innerWidth - UI_DIMENSIONS.CHAT_OFFSET - (globalThis.innerWidth - globalThis.document.body.offsetWidth)
  )
}

export const hasDownloadableData = (msgObj) => {
  if (!msgObj) return false
  return !!msgObj.sqlQuery || !!msgObj.sqlExecutionResult || !!msgObj.isChart
}

export const wrapTitle = (title, maxLength = 50) => {
  if (!title || typeof title !== 'string' || title.length <= maxLength) {
    return title
  }

  const words = title.split(' ')
  const lines = []
  let currentLine = ''

  for (const word of words) {
    if ((currentLine + word).length <= maxLength) {
      currentLine += (currentLine ? ' ' : '') + word
    } else {
      if (currentLine) lines.push(currentLine)
      currentLine = word
    }
  }
  if (currentLine) lines.push(currentLine)

  return lines.join('<br>')
}

/**
 * Converts markdown formatted text to plain text
 * - Removes bold markdown syntax (**text**)
 * - Converts markdown tables to tab-separated format
 * @param {string} text - The markdown text to convert
 * @returns {string} Plain text without markdown formatting
 */
export const convertMarkdownToPlainText = (text) => {
  if (!text) return text

  let plainText = text

  // Remove bold markdown (**text** -> text)
  plainText = plainText.replaceAll(/\*\*(.*?)\*\*/g, '$1')

  // Convert markdown tables to plain text
  // Table regex: matches header row, separator row, and data rows
  const tableRegex = /^\|.*\|$\n^\|[-\s|:]+\|$\n(^\|.*\|$\n?)+/gm

  plainText = plainText.replaceAll(tableRegex, (tableMatch) => {
    const lines = tableMatch
      .trim()
      .split('\n')
      .filter((line) => line.trim() !== '')

    // Find separator index
    let separatorIndex = -1
    for (let i = 1; i < lines.length; i++) {
      if (lines[i].includes('-') && lines[i].includes('|')) {
        separatorIndex = i
        break
      }
    }

    // Parse header
    const headerLine = lines[0]
    const headers = headerLine
      .split('|')
      .map((cell) => cell.trim().replaceAll(String.raw`\_`, '_'))
      .filter((cell) => cell !== '')

    // Parse data rows
    const dataStartIndex = separatorIndex === -1 ? 1 : separatorIndex + 1
    const dataLines = lines.slice(dataStartIndex)
    const rows = dataLines
      .filter((line) => line.includes('|'))
      .map((line) =>
        line
          .split('|')
          .map((cell) => cell.trim().replaceAll(String.raw`\_`, '_'))
          .filter((cell) => cell !== '')
      )
      .filter((row) => row.length > 0)

    // Convert to plain text format with tabs
    let tableText = headers.join('\t') + '\n'
    for (const row of rows) {
      tableText += row.join('\t') + '\n'
    }

    return tableText
  })

  return plainText
}
